import { ApplicationRef, Injectable, NgZone } from '@angular/core';
import { SwUpdate } from '@angular/service-worker';
import { Subscription, concat, first, interval } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class PromptUpdateService {
  isNewVersionAvailable: boolean = false;
  intervalSource = interval(15 * 60 * 1000); // every 15 mins
  intervalSubscription: Subscription;

  constructor(
    private appRef: ApplicationRef,
    private swUpdate: SwUpdate,
    private zone: NgZone
  ) {
    if (this.swUpdate.isEnabled) {
      this.checkForUpdate();

      // Allow the app to stabilize first, before starting polling for updates with `interval()`.
      const appIsStable$ = this.appRef.isStable.pipe(first(isStable => isStable === true));
      const everySixHours$ = interval(6 * 60 * 60 * 1000);
      const everySixHoursOnceAppIsStable$ = concat(appIsStable$, everySixHours$);
      everySixHoursOnceAppIsStable$.subscribe(() => this.swUpdate.checkForUpdate());
    }
  }

  /**
     * Checks for available updates for the service worker.
     *
     * This method unsubscribes from any existing interval subscription,
     * verifies service worker is enabled, runs an immediate update check,
     * and then sets up periodic checks every 15 minutes outside of Angular's zone.
     */
  checkForUpdate(): void {
    this.intervalSubscription?.unsubscribe();
    if (!this.swUpdate.isEnabled) {
      console.log('Service Worker não está habilitado');
      return;
    }

    // Verificar se o service worker está ativo
    navigator.serviceWorker.getRegistration().then(registration => {
      if (!registration || registration.installing || registration.waiting) {
        console.log('Service Worker ainda não está ativo');
        return;
      }

      // Continuar com a verificação
      this.performUpdateCheck();

      // Configurar o intervalo
      this.zone.runOutsideAngular(() => {
        this.intervalSubscription = this.intervalSource.subscribe(() => {
          this.performUpdateCheck();
        });
      });
    });
  }

  /**
     * Performs the actual update check and handles the result.
     */
  private async performUpdateCheck(): Promise<void> {
    console.log('Verificando atualização: ' + new Date().toLocaleString());
    try {
      // Check for updates
      this.isNewVersionAvailable = await this.swUpdate.checkForUpdate();

      console.log(this.isNewVersionAvailable ? 'Uma nova versão está disponível.' : 'Já está na versão mais recente.');
    } catch (error) {
      console.error('Erro ao verificar atualizações:', error);
      this.isNewVersionAvailable = false; // Resetar o estado em caso de erro
    }
  }

  applyUpdate(): void {
    // Reload the page to update to the latest version after the new version is activated
    this.swUpdate.activateUpdate()
      .then(() => document.location.reload())
      .catch(error => console.error('Erro ao aplicar atualizações:', error));
  }
}
